/**
 * Horizontal step bar for the annotation flow.
 *
 * Per `feature-spec.md` §3.4.1 — six dimension steps + a final Review step.
 * Each chip has one of three visual states (pending / active / complete).
 *
 * Click behaviour:
 *   - Active chip → no-op.
 *   - Visited chip (including any earlier or already-visited later) → jumps to it.
 *   - Future unvisited chip → no-op (forward gating).
 */
import React from 'react';

import { cn } from '../../../../../../lib/utils';
import { computeStepStates, type StepState } from './stepState';

export interface StepEntry {
  /** Display label, e.g. dimension name or "Review". */
  label: string;
  /** Step index in the flow (0-based). */
  index: number;
}

interface Props {
  steps: StepEntry[];
  currentStep: number;
  visited: Set<number>;
  satisfied: Set<number>;
  onStepClick: (index: number) => void;
}

const chipBase =
  'flex flex-col items-center gap-0.5 rounded-md border px-3 py-2 text-[12px] font-semibold transition-[background-color,color,border-color]';

const chipForState = (state: StepState, clickable: boolean): string => {
  if (state === 'active') {
    return cn(chipBase, 'border-primary bg-primary text-primary-foreground');
  }
  if (state === 'complete') {
    return cn(
      chipBase,
      'border-emerald-600 bg-emerald-600 text-white',
      clickable ? 'cursor-pointer hover:opacity-90' : 'cursor-default',
    );
  }
  return cn(
    chipBase,
    'border-border bg-card text-muted-foreground',
    clickable ? 'cursor-pointer hover:border-primary/30 hover:text-foreground' : 'cursor-default',
  );
};

const AnnotationStepBar: React.FC<Props> = ({
  steps,
  currentStep,
  visited,
  satisfied,
  onStepClick,
}) => {
  const states = computeStepStates({
    totalSteps: steps.length,
    currentStep,
    visited,
    satisfied,
  });

  return (
    <nav
      aria-label="Annotation steps"
      className="flex flex-wrap gap-2"
      data-testid="annotation-step-bar"
    >
      {steps.map((step) => {
        const state = states[step.index];
        // Forward gating: future unvisited steps aren't clickable.
        const clickable = state === 'complete' || visited.has(step.index);
        return (
          <button
            key={step.index}
            type="button"
            onClick={() => {
              if (clickable && state !== 'active') onStepClick(step.index);
            }}
            disabled={!clickable && state !== 'active'}
            aria-current={state === 'active' ? 'step' : undefined}
            data-testid={`step-chip-${step.index}`}
            data-state={state}
            className={chipForState(state, clickable && state !== 'active')}
          >
            <span className="text-[10px] uppercase tracking-wide opacity-80">
              Step {step.index + 1}
            </span>
            <span>{step.label}</span>
          </button>
        );
      })}
    </nav>
  );
};

export default AnnotationStepBar;
